/**
 * Create User Dialog
 *
 * Dialog for admins to create a new user in their organization.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AsyncButton } from '@/components/ui';
import { Icon, IconName } from '@/icons';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import { Button } from '@/components/ui/button';
import { UserRole } from '@/types/enums/UserRole';
import type { CreateUserInput, CreateUserOutput } from '@/types/generated';

interface CreateUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateUserDialog({ isOpen, onClose }: CreateUserDialogProps) {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>(UserRole.STUDENT);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usernameTouched, setUsernameTouched] = useState(false);

  const { execute, isLoading } = useOrchestrator<
    CreateUserInput,
    CreateUserOutput
  >(Action.CREATE_USER);

  const isValid =
    /^[a-zA-Z0-9]{2,50}$/.test(username.trim()) &&
    displayName.trim().length >= 1 &&
    displayName.trim().length <= 100 &&
    password.trim().length >= 6 &&
    (!email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));

  const handleCreate = async () => {
    if (!isValid) return;
    setError(null);

    const input: CreateUserInput = {
      username: username.trim(),
      display_name: displayName.trim(),
      password: password.trim(),
      role,
    };
    if (email.trim()) {
      input.email = email.trim();
    }

    const result = await execute(input);

    if (result.code === ResponseCode.SUCCESS) {
      resetForm();
      onClose();
    } else if (result.code === ResponseCode.DUPLICATE_RESOURCE) {
      setError('A user with this username already exists');
    } else if (result.code === ResponseCode.FORBIDDEN) {
      setError('You do not have permission to create users');
    }
  };

  const resetForm = () => {
    setUsername('');
    setDisplayName('');
    setPassword('');
    setEmail('');
    setRole(UserRole.STUDENT);
    setError(null);
    setUsernameTouched(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create User</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="new-user-username">Username</Label>
            <Input
              id="new-user-username"
              value={username}
              onChange={e => {
                setUsername(e.target.value);
                setError(null);
              }}
              onBlur={() => setUsernameTouched(true)}
              maxLength={50}
              placeholder="johndoe"
              disabled={isLoading}
            />
            {usernameTouched &&
            username.trim().length > 0 &&
            !/^[a-zA-Z0-9]{2,50}$/.test(username.trim()) ? (
              <p className="text-xs text-destructive">
                2-50 alphanumeric characters only, no spaces or symbols
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                2-50 alphanumeric characters, must be unique
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-user-display-name">Display Name</Label>
            <Input
              id="new-user-display-name"
              value={displayName}
              onChange={e => {
                setDisplayName(e.target.value);
                setError(null);
              }}
              maxLength={100}
              placeholder="John Doe"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-user-password">Password</Label>
            <div className="relative">
              <Input
                id="new-user-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  setError(null);
                }}
                maxLength={128}
                placeholder="Minimum 6 characters"
                disabled={isLoading}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <Icon
                  name={showPassword ? IconName.EYE_OFF : IconName.EYE}
                  size="sm"
                />
              </button>
            </div>
            {password.trim().length > 0 && password.trim().length < 6 && (
              <p className="text-xs text-destructive">
                Password must be at least 6 characters
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-user-email">Email (optional)</Label>
            <Input
              id="new-user-email"
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setError(null);
              }}
              placeholder="user@example.com"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-user-role">Role</Label>
            <select
              id="new-user-role"
              value={role}
              onChange={e => setRole(e.target.value)}
              disabled={isLoading}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value={UserRole.STUDENT}>Student</option>
              <option value={UserRole.MENTOR}>Mentor</option>
              <option value={UserRole.ADMIN}>Admin</option>
            </select>
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <AsyncButton
            onClick={handleCreate}
            disabled={!isValid || isLoading}
            isLoading={isLoading}
            icon={IconName.PLUS}
            loadingText="Creating..."
          >
            Create User
          </AsyncButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
